import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiUsers, FiCreditCard, FiMail, FiFileText, FiArrowLeft, FiSun, FiMoon, FiMenu, FiX, FiMessageCircle, FiGlobe, FiDollarSign } from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';

const NAV_ITEMS = [
  { path: '/admin', icon: <FiHome size={18} />, label: 'Dashboard', end: true },
  { path: '/admin/users', icon: <FiUsers size={18} />, label: 'Users' },
  { path: '/admin/subscriptions', icon: <FiCreditCard size={18} />, label: 'Credit Analytics' },
  { path: '/admin/web-source', icon: <FiGlobe size={18} />, label: 'Web Source' },
  { path: '/admin/credit-source', icon: <FiDollarSign size={18} />, label: 'Credit Buy Source' },
  { path: '/admin/messages', icon: <FiMessageCircle size={18} />, label: 'Messages' },
  { path: '/admin/email-subscribers', icon: <FiMail size={18} />, label: 'Email Subscribers' },
  { path: '/admin/blog', icon: <FiFileText size={18} />, label: 'Blog Manager' }
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div style={styles.layout}>
      {/* Mobile header */}
      {isMobile && (
        <div style={styles.mobileHeader}>
          <button onClick={() => setMenuOpen(!menuOpen)} style={styles.menuBtn}>
            {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={styles.brand}>ReplyCraft</span>
            <span style={styles.adminBadge}>Admin</span>
          </div>
          <button onClick={toggleTheme} style={styles.themeToggleBtn}>
            {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
          </button>
        </div>
      )}

      {/* Sidebar / Mobile Menu */}
      {(!isMobile || menuOpen) && (
        <>
          {isMobile && <div onClick={() => setMenuOpen(false)} style={styles.overlay} />}
          <aside style={{ ...styles.sidebar, ...(isMobile ? styles.sidebarMobile : {}) }}>
            {!isMobile && (
              <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={styles.brand}>ReplyCraft</h2>
                  <span style={styles.adminBadge}>Admin</span>
                </div>
                <button onClick={toggleTheme} style={styles.themeToggleBtn}>
                  {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
                </button>
              </div>
            )}

            <nav style={styles.nav}>
              {NAV_ITEMS.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={() => isMobile && setMenuOpen(false)}
                  style={({ isActive }) => ({ ...styles.navItem, ...(isActive ? styles.navItemActive : {}) })}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div style={styles.footer}>
              <button onClick={() => navigate('/')} style={styles.backBtn}>
                <FiArrowLeft size={16} /> Back to App
              </button>
            </div>
          </aside>
        </>
      )}

      <main style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
        {children}
      </main>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  mobileHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' },
  menuBtn: { background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', padding: 4, display: 'flex' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 },
  sidebar: { width: 240, background: 'var(--bg-secondary)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', zIndex: 999 },
  sidebarMobile: { top: 0, left: 0 },
  header: { padding: '20px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  themeToggleBtn: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  brand: { fontSize: 18, fontWeight: 700, color: 'var(--accent)' },
  adminBadge: { padding: '2px 8px', background: 'var(--danger)', color: 'white', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' },
  nav: { flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 14, textDecoration: 'none', transition: 'background 0.15s' },
  navItemActive: { background: 'var(--bg-primary)', color: 'var(--accent)', fontWeight: 600 },
  footer: { padding: '12px', borderTop: '1px solid var(--border)' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' },
  main: { flex: 1, marginLeft: 240, padding: '32px', overflowY: 'auto' },
  mainMobile: { marginLeft: 0, padding: '16px' }
};
